/**
 * Sticky product form that appears on scroll
 * Shows a sticky add-to-cart bar at the bottom when user scrolls past product details
 */

export class StickyProductForm extends HTMLElement {
  static TRANSITION = 'transform 0.3s ease'
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'

  lastScrollY: number
  ticking: boolean
  isVisible: boolean
  isScrolled: boolean
  bottomOffset: number
  elementHeight: number
  stickyFormElement: HTMLElement | null
  /** Cached scroll threshold — see `measureLayout`. */
  uiThreshold: number

  constructor() {
    super()

    // State only. A custom element constructor must not inspect layout or children —
    // they are not guaranteed to exist yet — and `connectedCallback` already performs
    // every measurement this previously duplicated.
    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = false
    this.isScrolled = false
    this.bottomOffset = 0
    this.elementHeight = 0
    this.stickyFormElement = null
    this.uiThreshold = window.innerHeight * 1.3

    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
  }

  get hiddenPosition() {
    return 'translateY(100%)'
  }

  get visiblePosition() {
    return 'translateY(-100%)'
  }

  /**
   * `uiThreshold` was previously a `static get UI_THRESHOLD()` read from inside the
   * scroll rAF, so every frame ran a document-wide `querySelector` plus a
   * `getBoundingClientRect()` on the whole product section — a full style + layout
   * flush roughly 60x/second while scrolling a PDP on mobile. `.product-main` really
   * does exist (`sections/product/product-main.liquid`), so the expensive branch was
   * always the one taken. The value only changes when layout does, so it is measured
   * on connect and on resize instead.
   */
  measureLayout() {
    const productMain = document.querySelector('.product-main')
    this.uiThreshold = productMain
      ? (productMain.getBoundingClientRect().height / 4) * 3
      : window.innerHeight * 1.3

    this.bottomOffset = parseInt(window.getComputedStyle(this).bottom.replace('px', ''))
    this.stickyFormElement = this.querySelector('#sticky-product-form')
    this.elementHeight = this.stickyFormElement
      ? this.stickyFormElement.getBoundingClientRect().height
      : this.getBoundingClientRect().height
  }

  handleResize() {
    this.measureLayout()
  }

  connectedCallback() {
    this.style.transition = StickyProductForm.TRANSITION

    this.measureLayout()

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize, { passive: true })

    // Start hidden
    this.style.transform = this.hiddenPosition
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  }

  showStickyForm() {
    if (!this.isVisible) {
      this.style.transform = this.visiblePosition
      this.isVisible = true
      this.setAttribute('data-visible', 'visible')
    }
  }

  hideStickyForm() {
    if (this.isVisible) {
      this.style.transform = this.hiddenPosition
      this.isVisible = false
      this.removeAttribute('data-visible')
    }
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY

        // Show sticky bar if we're past the threshold
        if (currentScroll > this.uiThreshold) {
          this.showStickyForm()
        } else {
          this.hideStickyForm()
        }
        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }
}
